import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';

export default function InvestorNetworkPage() {
  const investorTypes = [
    {
      title: 'Angel Investors',
      description: 'High-net-worth individuals who invest their personal funds in early-stage companies',
      investment: '$25,000 - $500,000',
      stage: 'Seed to Early Stage',
      benefits: [
        'Mentorship and industry expertise',
        'Flexible terms compared to VCs',
        'Quick decision-making process',
        'Often invest locally'
      ]
    },
    {
      title: 'Venture Capital Firms',
      description: 'Professional investment firms managing pooled funds from multiple investors',
      investment: '$500,000 - $10 million+',
      stage: 'Series A and Beyond',
      benefits: [
        'Larger investment amounts',
        'Strategic guidance and connections',
        'Follow-on funding potential',
        'Credibility and validation'
      ]
    },
    {
      title: 'Corporate Investors',
      description: 'Established companies investing in startups aligned with their strategic interests',
      investment: 'Varies widely',
      stage: 'Growth Stage',
      benefits: [
        'Industry expertise and resources',
        'Potential partnership opportunities',
        'Access to distribution channels',
        'Long-term strategic support'
      ]
    }
  ];

  const localInvestors = [
    {
      name: 'Borderplex Alliance',
      description: 'Regional economic development organization connecting businesses with investors',
      focus: 'Manufacturing, logistics, technology, healthcare',
      location: 'El Paso, TX'
    },
    {
      name: 'Tech Bloc',
      description: 'San Antonio-based organization supporting tech startups across Texas',
      focus: 'Technology, software, innovation',
      location: 'San Antonio, TX (serves El Paso region)'
    },
    {
      name: 'Capital Factory',
      description: 'Texas\' largest coworking space and startup accelerator with investor network',
      focus: 'Technology, SaaS, mobile apps',
      location: 'Austin, TX (virtual programs available)'
    },
    {
      name: 'Texas Venture Labs',
      description: 'University of Texas investment fund and accelerator program',
      focus: 'Student and alumni ventures, technology',
      location: 'Austin, TX'
    }
  ];

  const pitchComponents = [
    {
      section: 'Problem Statement',
      duration: '30 seconds',
      description: 'Clearly articulate the problem your business solves',
      tips: ['Use real-world examples', 'Quantify the problem size', 'Make it relatable']
    },
    {
      section: 'Solution',
      duration: '1 minute',
      description: 'Explain your product/service and how it solves the problem',
      tips: ['Demonstrate uniqueness', 'Show traction or validation', 'Keep it simple']
    },
    {
      section: 'Market Opportunity',
      duration: '45 seconds',
      description: 'Define your target market and growth potential',
      tips: ['Use credible data sources', 'Show market size', 'Identify your niche']
    },
    {
      section: 'Business Model',
      duration: '45 seconds',
      description: 'Explain how you make money',
      tips: ['Clear revenue streams', 'Pricing strategy', 'Unit economics']
    },
    {
      section: 'Traction',
      duration: '45 seconds',
      description: 'Share key metrics, customers, and milestones',
      tips: ['Use specific numbers', 'Show growth trajectory', 'Highlight achievements']
    },
    {
      section: 'Team',
      duration: '30 seconds',
      description: 'Introduce key team members and their expertise',
      tips: ['Highlight relevant experience', 'Show complementary skills', 'Demonstrate commitment']
    },
    {
      section: 'Financial Projections',
      duration: '45 seconds',
      description: 'Present realistic financial forecasts',
      tips: ['3-5 year projections', 'Key assumptions', 'Path to profitability']
    },
    {
      section: 'The Ask',
      duration: '30 seconds',
      description: 'State how much you\'re raising and how you\'ll use it',
      tips: ['Specific amount', 'Clear use of funds', 'Expected milestones']
    }
  ];

  const preparationSteps = [
    {
      icon: 'ri-file-text-line',
      title: 'Develop Your Pitch Deck',
      description: 'Create a compelling 10-15 slide presentation covering problem, solution, market, team, and financials.'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Prepare Financial Models',
      description: 'Build detailed financial projections showing revenue, expenses, and cash flow for 3-5 years.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Get Legal Documents Ready',
      description: 'Have incorporation papers, cap table, and term sheet templates prepared in advance.'
    },
    {
      icon: 'ri-team-line',
      title: 'Build Your Advisory Board',
      description: 'Recruit experienced advisors who can provide credibility and open doors to investors.'
    },
    {
      icon: 'ri-search-line',
      title: 'Research Target Investors',
      description: 'Identify investors who focus on your industry, stage, and geography. Study their portfolio companies.'
    },
    {
      icon: 'ri-presentation-line',
      title: 'Practice Your Pitch',
      description: 'Rehearse extensively. Get feedback from mentors and refine your delivery until it\'s natural.'
    }
  ];

  const networkingTips = [
    'Attend local startup events and pitch competitions in El Paso and the Borderplex region',
    'Join entrepreneur organizations like the El Paso Hispanic Chamber of Commerce',
    'Participate in accelerator programs that provide investor introductions',
    'Leverage warm introductions through mutual connections rather than cold outreach',
    'Build relationships before you need funding—investors invest in people they know',
    'Follow up promptly and professionally after every investor meeting',
    'Be transparent about challenges and how you\'re addressing them',
    'Show momentum and progress between investor conversations'
  ];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://elpasotexasbusiness.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Resources",
        "item": "https://elpasotexasbusiness.com/resources"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Investor Network",
        "item": "https://elpasotexasbusiness.com/resources/investor-network"
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="El Paso Investor Network - Angel Investors & Venture Capital"
        description="Connect with investors in El Paso and West Texas. Find angel investors, venture capital firms, and funding sources for your startup. Learn how to pitch and secure investment."
        canonical="https://elpasotexasbusiness.com/resources/investor-network"
        ogImage="https://public.readdy.ai/ai/img_res/72bdf69b4567e1a54bfeb0ce71e1dd6f.jpg"
      />
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        
        <main className="flex-grow">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-6">
              <div className="mb-8">
                <Link to="/resources" className="text-[#D2691E] hover:text-[#B8571A] font-semibold flex items-center space-x-2 transition-colors whitespace-nowrap">
                  <i className="ri-arrow-left-line"></i>
                  <span>Back to Resources</span>
                </Link>
              </div>
            </div>
          </nav>

          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-16">
              <h1 className="text-6xl font-bold text-gray-900 mb-6">Investor Network</h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl">
                Connect with angel investors, venture capital firms, and corporate investors in the El Paso and Borderplex region. 
                Learn how to prepare your pitch and build relationships with potential investors.
              </p>
            </div>

            <section className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Types of Investors</h2>
              <div className="space-y-8">
                {investorTypes.map((type, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-10">
                    <div className="flex items-start justify-between mb-4 flex-wrap gap-4">
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{type.title}</h3>
                        <p className="text-base text-gray-600 leading-relaxed">{type.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500 font-semibold mb-1">Typical Investment</p>
                        <p className="text-[#D2691E] font-bold text-lg whitespace-nowrap">{type.investment}</p>
                        <p className="text-xs text-gray-500 mt-2">{type.stage}</p>
                      </div>
                    </div>
                    <div className="mt-6">
                      <h4 className="font-bold text-gray-900 mb-4 text-base">Key Benefits</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        {type.benefits.map((benefit, idx) => (
                          <div key={idx} className="flex items-start space-x-3">
                            <i className="ri-check-line text-[#D2691E] text-xl mt-0.5"></i>
                            <span className="text-sm text-gray-700">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Local & Regional Investor Networks</h2>
              <div className="grid md:grid-cols-2 gap-8">
                {localInvestors.map((investor, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{investor.name}</h3>
                    <p className="text-sm text-gray-600 mb-6 leading-relaxed">{investor.description}</p>
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-gray-500 font-semibold mb-1">Focus Areas</p>
                        <p className="text-sm text-gray-700">{investor.focus}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-semibold mb-1">Location</p>
                        <p className="text-sm text-gray-700">{investor.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Crafting Your Investor Pitch</h2>
              <p className="text-base text-gray-600 mb-8 leading-relaxed max-w-4xl">
                A typical investor pitch is 5-7 minutes. Here's how to structure your presentation for maximum impact:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {pitchComponents.map((component, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-900">{component.section}</h3>
                      <span className="bg-[#D2691E] text-white px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap">
                        {component.duration}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-4 leading-relaxed">{component.description}</p>
                    <div className="space-y-2">
                      {component.tips.map((tip, idx) => (
                        <div key={idx} className="flex items-start space-x-2">
                          <i className="ri-arrow-right-s-line text-[#D2691E] text-base mt-0.5"></i>
                          <span className="text-xs text-gray-700">{tip}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Preparation Steps</h2>
              <div className="grid md:grid-cols-3 gap-8">
                {preparationSteps.map((step, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8">
                    <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center mb-6">
                      <i className={`${step.icon} text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{step.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-20">
              <div className="bg-[#1B3A3A] rounded-3xl p-12">
                <h2 className="text-4xl font-bold text-white mb-8">Networking & Relationship Building</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {networkingTips.map((tip, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-[#D2691E] rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-white text-base"></i>
                      </div>
                      <p className="text-sm text-white/90 leading-relaxed">{tip}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="mb-20">
              <div className="bg-[#FAFAF8] rounded-3xl p-12">
                <h2 className="text-4xl font-bold text-gray-900 mb-8">What Investors Look For</h2>
                <div className="grid md:grid-cols-3 gap-8">
                  <div>
                    <h3 className="font-bold text-xl text-gray-900 mb-4">Strong Team</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Investors bet on people first. They want to see a committed, experienced team with complementary skills and the ability to execute.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-gray-900 mb-4">Large Market</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Your addressable market should be large enough to support significant growth. Investors want to see billion-dollar market opportunities.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-gray-900 mb-4">Traction</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Evidence of product-market fit through customer adoption, revenue growth, or other key metrics that validate your business model.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-gray-900 mb-4">Competitive Advantage</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      A defensible moat—whether through technology, network effects, brand, or other barriers to entry that protect your market position.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-gray-900 mb-4">Scalability</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      The ability to grow revenue significantly without proportional increases in costs. Software and technology businesses excel here.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-gray-900 mb-4">Clear Exit Path</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Investors need to see how they'll eventually realize returns, whether through acquisition, IPO, or other liquidity events.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <div className="bg-[#FAFAF8] rounded-3xl p-12 text-center">
              <h3 className="text-4xl font-bold text-gray-900 mb-4">Ready to Connect with Investors?</h3>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Our team can help you refine your pitch, make introductions, and navigate the fundraising process for your El Paso business.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-3 bg-[#D2691E] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#B8571A] transition-all shadow-lg cursor-pointer whitespace-nowrap"
              >
                <span>Contact Us</span>
                <i className="ri-arrow-right-line text-xl"></i>
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}